import qs from 'qs';
import { USER_DETAILS, USER_PREFERENCES } from '../../constants/endpoints';
import {
  PREFERENCES_BASE,
  SAVE_ERROR,
  SAVE_SUCCESS,
  EMAIL_MISMATCH,
} from '../../constants/preferences';
import { titan } from '../../lib/productAnalytics';
import { EMAIL_ADDRESS_PATTERN } from '../../constants/validationPatterns';
import { logData } from '../analytics';
import { EVENTS, EVENT_VALUES } from '../../constants/noflake/noflakeSchema';
import { PROMOTION_IDS, TITAN_EVENTS } from '../../constants/titan';

export const RECEIVE_USER_PREFERENCES = 'RECEIVE_USER_PREFERENCES';
export const RECEIVE_USER_PREFERENCES_ENABLED = 'RECEIVE_USER_PREFERENCES_ENABLED';
export const RECEIVE_PREFERENCES_SAVE_STATE = 'RECEIVE_PREFERENCES_SAVE_STATE';
export const RECEIVE_IS_SAVING_PREFERENCES = 'RECEIVE_IS_SAVING_PREFERENCES';
export const RECEIVE_USER_PREFERENCES_FETCH_ERROR = 'RECEIVE_USER_PREFERENCES_FETCH_ERROR';
export const RECEIVE_USER_PREFERENCES_IS_SIGNING_UP = 'RECEIVE_USER_PREFERENCES_IS_SIGNING_UP';
export const RECEIVE_USER_PREFERENCES_EMAIL_ADDRESS = 'RECEIVE_USER_PREFERENCES_EMAIL_ADDRESS';
export const RECEIVE_USER_PREFERENCES_SKIP_EMAIL_INPUT =
  'RECEIVE_USER_PREFERENCES_SKIP_EMAIL_INPUT';
export const RECEIVE_USER_PREFERENCES_APPLIED = 'RECEIVE_USER_PREFERENCES_APPLIED';
export const RECEIVE_USER_PREFERENCES_FORM_ANALYTICS_SENT =
  'RECEIVE_USER_PREFERENCES_FORM_ANALYTICS_SENT';

export const receiveIsSavingPreferences = () => ({ type: RECEIVE_IS_SAVING_PREFERENCES });

export const receiveUserPreferencesEnabled = (enabled) => ({
  type: RECEIVE_USER_PREFERENCES_ENABLED,
  data: {
    enabled,
  },
});

export const receiveUserPrefEmailAddress = (emailAddress) => ({
  type: RECEIVE_USER_PREFERENCES_EMAIL_ADDRESS,
  data: {
    emailAddress,
    validForm: new RegExp(EMAIL_ADDRESS_PATTERN).test(emailAddress),
  },
});

export const fetchUserDetails = () => async (dispatch, getState) => {
  const {
    userPreferences: { enabled } = {},
    page: {
      browserFingerprint,
      giftOrder,
      initialPageLoadParams: { email: queryParamEmail } = {},
      locale,
    },
    landingPage: { landingForm: { email: landingPageEmail } = {} } = {},
    orderInfo: { currentOrderNumber: orderNumber } = {},
    retailerSettings: { name: retailerName } = {},
  } = getState();

  if (giftOrder || ['en_US', 'en_CA'].indexOf(locale) === -1) {
    dispatch(receiveUserPreferencesEnabled(false));
    return;
  }

  if (!enabled) {
    dispatch(
      logData({
        type: EVENTS.DEBUG,
        value: EVENT_VALUES[EVENTS.DEBUG].preferences_eligibility_false,
      }),
    );
    return;
  }

  try {
    const response = await fetch(
      USER_DETAILS({
        retailerName,
        params: qs.stringify({
          orderNumber,
          locale,
        }),
      }),
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'narvar-tfid': browserFingerprint,
        },
      },
    );
    const { user_preference: preferences, user_profile: profile } = await response.json();
    await dispatch({
      type: RECEIVE_USER_PREFERENCES,
      data: {
        preferences,
        profile,
      },
    });
  } catch (err) {
    console.log(err);
    await dispatch({ type: RECEIVE_USER_PREFERENCES_FETCH_ERROR });
  } finally {
    dispatch({
      type: RECEIVE_USER_PREFERENCES_SKIP_EMAIL_INPUT,
      data: {
        skipEmailInput: landingPageEmail && landingPageEmail !== queryParamEmail,
      },
    });
  }
};

export const saveUserPreferences = () => async (dispatch, getState) => {
  const {
    page: { browserFingerprint, locale } = {},
    landingPage: { landingForm: { email: landingPageEmail } = {} } = {},
    orderInfo: { currentOrderNumber: orderNumber } = {},
    retailerSettings: { name: retailerName } = {},
    returnOptions: {
      selectedOptionMethodKey,
      returnOptionMethods: { [selectedOptionMethodKey]: { id: selectedMethodId } = {} } = {},
    } = {},
    userPreferences: { preferences, emailAddress, skipEmailInput } = {},
  } = getState();
  try {
    dispatch(receiveIsSavingPreferences());
    const response = await fetch(
      USER_PREFERENCES({
        retailerName,
        params: qs.stringify({
          locale,
        }),
      }),
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'narvar-tfid': browserFingerprint,
        },
        body: JSON.stringify({
          order_number: orderNumber,
          user_email: skipEmailInput ? landingPageEmail : emailAddress,
          user_preference: {
            ...PREFERENCES_BASE,
            ...preferences,
            return_methods: [selectedMethodId],
          },
        }),
      },
    );
    const { success, errors } = await response.json();
    if (success) {
      titan.event(TITAN_EVENTS.PROMOTION_CLICKED, { promotion_id: PROMOTION_IDS.USER_PREFERENCES });
      dispatch(
        logData({
          type: EVENTS.SUCCESS,
          value: EVENT_VALUES[EVENTS.SUCCESS].preferences_save,
        }),
      );
    } else {
      dispatch(
        logData({
          type: EVENTS.ERROR,
          value: EVENT_VALUES[EVENTS.ERROR].preferences_save,
        }),
      );
    }

    const isEmailMismatchError = errors && errors.some((error) => error.code === '101');
    let saveState;
    if (success) {
      saveState = SAVE_SUCCESS;
    } else if (isEmailMismatchError) {
      saveState = EMAIL_MISMATCH;
    } else {
      saveState = SAVE_ERROR;
    }

    dispatch({
      type: RECEIVE_PREFERENCES_SAVE_STATE,
      data: {
        saveState,
      },
    });
    return saveState;
  } catch (err) {
    dispatch({
      type: RECEIVE_PREFERENCES_SAVE_STATE,
      data: {
        saveState: SAVE_ERROR,
      },
    });
    dispatch(
      logData({
        type: EVENTS.ERROR,
        value: EVENT_VALUES[EVENTS.ERROR].preferences_save,
      }),
    );
    return SAVE_ERROR;
  }
};

export const receiveUserPrefApplied = (applied) => ({
  type: RECEIVE_USER_PREFERENCES_APPLIED,
  data: {
    applied,
  },
});

export const receiveUserPreferencesCheckboxToggle = () => (dispatch, getState) => {
  const { userPreferences: { isSigningUp, skipEmailInput, formAnalyticsSent } = {} } = getState();
  const nextIsSigningUp = !isSigningUp;
  const displayEmailInput = nextIsSigningUp && !skipEmailInput;

  dispatch({
    type: RECEIVE_USER_PREFERENCES_IS_SIGNING_UP,
    data: {
      isSigningUp: nextIsSigningUp,
      displayEmailInput,
    },
  });

  if (!formAnalyticsSent) {
    dispatch(
      logData({
        type: EVENTS.CLICK,
        value: EVENT_VALUES[EVENTS.CLICK].preferences_checkbox,
      }),
    );

    if (displayEmailInput) {
      dispatch(
        logData({
          type: EVENTS.COMPONENT_LOAD,
          value: EVENT_VALUES[EVENTS.COMPONENT_LOAD].preferences_email_input,
        }),
      );
    }

    dispatch({
      type: RECEIVE_USER_PREFERENCES_FORM_ANALYTICS_SENT,
    });
  }
};
